import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getFullCdnUrl } from "../config";
import { extractVol } from "../utils";

const PlayerContext = createContext(null);

export function durationToSeconds(duration) {
  if (typeof duration === "number") return duration;
  if (!duration) return 0;
  const parts = String(duration).split(":").map(Number);
  if (parts.some(Number.isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

/** 把后端单集对象转换成播放器需要的形状。 */
export function toPlayerEpisode(episode, podcastSession) {
  const session = podcastSession || "";
  const path = `cdn/${episode.PodcastID}/${episode.ID}/${episode.AudioFilename}`;
  return {
    id: episode.ID,
    title: episode.Title,
    vol: extractVol(episode.Title),
    memberOnly: !!episode.IsPaid,
    durationSec: durationToSeconds(episode.Duration),
    audioUrl: getFullCdnUrl(
      session ? `${path}?podcast_session=${session}` : path
    ),
  };
}

/** 全站唯一的音频播放器：持有 <audio>，管理当前单集、播放列表、倍速与定时结束。 */
export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  if (!audioRef.current && typeof Audio !== "undefined") {
    audioRef.current = new Audio();
    audioRef.current.preload = "metadata";
  }

  const [episode, setEpisode] = useState(null);
  const [queue, setQueue] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeedState] = useState(1);
  const [timer, setTimerState] = useState(null); // null | 'episode' | 分钟数
  const [toastMsg, setToastMsg] = useState(null);
  const [toastVariant, setToastVariant] = useState("success");

  const sleepTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const stateRef = useRef({});
  stateRef.current = { episode, queue, timer, speed };

  const toast = useCallback((msg, variant = "success") => {
    setToastMsg(msg);
    setToastVariant(variant);
    clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToastMsg(null), 3200);
  }, []);

  const clearSleepTimer = useCallback(() => {
    clearTimeout(sleepTimeoutRef.current);
    sleepTimeoutRef.current = null;
  }, []);

  const startEpisode = useCallback((ep) => {
    const audio = audioRef.current;
    if (!audio || !ep) return;
    setEpisode(ep);
    setCurrentTime(0);
    setDuration(ep.durationSec || 0);
    audio.src = ep.audioUrl;
    audio.playbackRate = stateRef.current.speed;
    audio.play().catch(() => setPlaying(false));
  }, []);

  const play = useCallback(
    (ep) => {
      const audio = audioRef.current;
      if (!audio || !ep) return;
      const cur = stateRef.current.episode;
      setQueue((q) => (q.some((x) => x.id === ep.id) ? q : [...q, ep]));
      if (cur && cur.id === ep.id) {
        audio.play().catch(() => setPlaying(false));
        return;
      }
      startEpisode(ep);
    },
    [startEpisode]
  );

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !stateRef.current.episode) return;
    if (audio.paused) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  }, []);

  const seek = useCallback((sec) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, sec);
    setCurrentTime(audio.currentTime);
  }, []);

  const skip = useCallback((delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = audio.duration || stateRef.current.episode?.durationSec || 0;
    audio.currentTime = Math.min(max, Math.max(0, audio.currentTime + delta));
    setCurrentTime(audio.currentTime);
  }, []);

  const setSpeed = useCallback((s) => {
    setSpeedState(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  }, []);

  const setTimer = useCallback(
    (value) => {
      clearSleepTimer();
      setTimerState(value);
      if (typeof value === "number") {
        sleepTimeoutRef.current = setTimeout(() => {
          audioRef.current?.pause();
          setTimerState(null);
          toast("定时结束，已停止播放");
        }, value * 60 * 1000);
        toast(`${value} 分钟后停止播放`);
      } else if (value === "episode") {
        toast("播完本集后停止");
      } else {
        toast("已关闭定时");
      }
    },
    [clearSleepTimer, toast]
  );

  const next = useCallback(() => {
    const { episode: cur, queue: q } = stateRef.current;
    if (!cur) return;
    const idx = q.findIndex((x) => x.id === cur.id);
    const nextEp = q[idx + 1];
    if (nextEp) startEpisode(nextEp);
  }, [startEpisode]);

  const queueAdd = useCallback(
    (ep) => {
      setQueue((q) => {
        if (q.some((x) => x.id === ep.id)) return q;
        return [...q, ep];
      });
      toast("已加入播放列表");
    },
    [toast]
  );

  const queueRemove = useCallback((id) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  }, []);

  const queueSelect = useCallback(
    (id) => {
      const target = stateRef.current.queue.find((x) => x.id === id);
      if (!target) return;
      if (stateRef.current.episode?.id === id) {
        toggle();
        return;
      }
      startEpisode(target);
    },
    [startEpisode, toggle]
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => {
      if (audio.duration && Number.isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      const { timer: t, episode: cur, queue: q } = stateRef.current;
      if (t === "episode") {
        setTimerState(null);
        return;
      }
      const idx = q.findIndex((x) => x.id === cur?.id);
      const nextEp = q[idx + 1];
      if (nextEp) startEpisode(nextEp);
    };
    const onError = () => {
      const cur = stateRef.current.episode;
      setPlaying(false);
      toast(
        cur?.memberOnly
          ? "无法播放该单集：会员权限校验未通过，请检查会员是否在有效期内"
          : "音频加载失败，请稍后重试",
        "error"
      );
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
    };
  }, [startEpisode, toast]);

  useEffect(() => () => {
    clearTimeout(sleepTimeoutRef.current);
    clearTimeout(toastTimeoutRef.current);
    audioRef.current?.pause();
  }, []);

  const value = useMemo(
    () => ({
      episode,
      queue,
      playing,
      currentTime,
      duration,
      speed,
      timer,
      toastMsg,
      toastVariant,
      play,
      toggle,
      seek,
      skip,
      next,
      setSpeed,
      setTimer,
      queueAdd,
      queueRemove,
      queueSelect,
      toast,
    }),
    [
      episode, queue, playing, currentTime, duration, speed, timer,
      toastMsg, toastVariant,
      play, toggle, seek, skip, next, setSpeed, setTimer,
      queueAdd, queueRemove, queueSelect, toast,
    ]
  );

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer 必须在 PlayerProvider 内使用");
  return ctx;
}
